import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Typography, Snackbar, Alert, Backdrop, CircularProgress 
} from '@mui/material';
import { 
  Add as AddIcon, Print as PrintIcon, Draw as DrawIcon, 
  Save as SaveIcon, CloudUpload as CloudUploadIcon, PhotoCamera as PhotoIcon, 
  ExitToApp as ExitIcon
} from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FixedPage from './FixedPage'; 
import { useAuth } from '../context/AuthContext';
import DictationButton from './DictationButton';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// --- UTILIDADES ---
const processImageUrl = (url) => {
    if (!url) return '';
    let finalUrl = url.startsWith('data:') ? url : url.replace('http://', 'https://');
    if (!finalUrl.startsWith('http') && !finalUrl.startsWith('data:')) {
        const cleanPath = finalUrl.replace(/\\/g, '/');
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        finalUrl = `${baseUrl}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
    }
    return finalUrl.includes('?') ? finalUrl : `${finalUrl}?t=${new Date().getTime()}`;
};

const imageToBase64 = async (url) => {
    if (!url || url.startsWith('data:')) return url;
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/proxy-image?url=${encodeURIComponent(url)}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) return (await res.json()).base64;
        return url;
    } catch (e) { return url; }
};

// --- CONFIGURACIÓN DEL EDITOR (SACADO AFUERA PARA ARREGLAR CTRL+Z) ---
const EditorComponent = ({ value, onChange }) => (
    <Editor
        tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js"
        onEditorChange={onChange}
        value={value}
        init={{
          height: '100%',
          menubar: false, // Menú superior oculto para limpieza (usamos toolbar)
          inline: true,   // Modo inline (flotante)
          fixed_toolbar_container: '#toolbar-container', // Barra anclada arriba
          placeholder: 'Cargando datos o escriba aquí...',
          
          // PLUGINS POTENCIADOS
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 
            'searchreplace', 'visualblocks', 'code', 'fullscreen', 
            'insertdatetime', 'media', 'table', 'help', 'wordcount'
          ],
          
          // BARRA DE HERRAMIENTAS COMPLETA
          toolbar: 
            'undo redo | searchreplace | blocks fontfamily fontsize | ' +
            'bold italic underline forecolor | ' +
            'alignleft aligncenter alignright alignjustify | ' +
            'bullist numlist outdent indent | ' +
            'removeformat | help',
            
          fontsize_formats: '8pt 10pt 11pt 12pt 14pt 18pt 24pt',
          content_style: 'body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #000; }',
          
          // CONFIGURACIÓN EXTRA
          browser_spellcheck: true, // Activa corrector de Chrome/Edge
          contextmenu: "link image table", // Click derecho útil
          valid_elements: '*[*]', // Permite todo el HTML
        }}
    />
);

// --- GENERADOR DE HTML ---
const generateMedicalHistoryHTML = (patient, historyData, evolutions, recipes, activePendings) => {
    if (!patient) return '';

    const bg = historyData?.background || {}; 
    const go = historyData?.gynecoObstetric || {};
    const habits = bg.habits || {};
    const allergies = historyData?.allergies || "Niega";

    const lastIllnessEntry = historyData?.currentIllnessHistory && historyData.currentIllnessHistory.length > 0 
        ? historyData.currentIllnessHistory[0] : null;
    const lastPhysicalEntry = historyData?.physicalExamHistory && historyData.physicalExamHistory.length > 0 
        ? historyData.physicalExamHistory[0] : null;
    const lastEvo = evolutions && evolutions.length > 0 ? evolutions[0] : {}; 
    const lastRecipe = recipes && recipes.length > 0 ? recipes[0] : {};       

    const sectionTitleStyle = "background-color: #e0e0e0; padding: 5px 10px; border-left: 5px solid #2c3e50; margin-top: 15px; margin-bottom: 10px; font-weight: bold; font-family: Arial, sans-serif; font-size: 11pt;";
    const labelStyle = "font-weight: bold; color: #444;";
    const textStyle = "font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.5;";
    const vitalBoxStyle = "border: 1px solid #ccc; padding: 5px; text-align: center; border-radius: 4px; background: #fff;";

    let html = `
      <h2 style="text-align: center; text-transform: uppercase; text-decoration: underline; margin-bottom: 25px; font-family: Arial;">
        HISTORIA MÉDICA INTEGRAL
      </h2>
    `;

    // 1. ANTECEDENTES
    html += `<div style="${sectionTitleStyle}">1. ANTECEDENTES</div>`;
    html += `<div style="${textStyle}">`;
    if (allergies && allergies.trim()) html += `<p style="color: #d32f2f;"><strong>⚠ ALERGIAS:</strong> ${allergies.toUpperCase()}</p>`;
    if (bg.previousPathologies) html += `<p><span style="${labelStyle}">Patologías Personales:</span> ${bg.previousPathologies}</p>`;
    if (bg.surgical) html += `<p><span style="${labelStyle}">Quirúrgicos:</span> ${bg.surgical}</p>`;
    if (bg.family && (bg.family.parents || bg.family.children)) html += `<p><span style="${labelStyle}">Familiares:</span> ${bg.family.parents || ''} ${bg.family.children ? `(Hijos: ${bg.family.children})` : ''}</p>`;
    
    let habitosStr = [];
    if (habits.tabaquico?.active) habitosStr.push(`Tabaquismo (${habits.tabaquico.notes || 'Si'})`);
    if (habits.alcoholico?.active) habitosStr.push(`Alcohol (${habits.alcoholico.notes || 'Si'})`);
    if (habits.cafeinico?.active) habitosStr.push(`Cafeína (${habits.cafeinico.notes || 'Si'})`);
    if (habitosStr.length > 0) html += `<p><span style="${labelStyle}">Hábitos:</span> ${habitosStr.join(', ')}</p>`;

    if (go.menarche?.age || go.gestas || go.lastMenstruation) html += `<p><span style="${labelStyle}">Gineco-Obstétricos:</span> FUR: ${go.lastMenstruation || '--'} | Gestas: ${go.gestas || '--'}</p>`;
    if (bg.currentMedications && bg.currentMedications.length > 0) {
        const medsBase = bg.currentMedications.map(m => m.text).join(', ');
        html += `<p><span style="${labelStyle}">Medicamentos de Base:</span> ${medsBase}</p>`;
    }
    html += `</div>`;

    // 2. ENFERMEDAD ACTUAL
    if (lastIllnessEntry) {
        html += `<div style="${sectionTitleStyle}">2. ENFERMEDAD ACTUAL (${new Date(lastIllnessEntry.date).toLocaleDateString()})</div>`;
        html += `<div style="${textStyle}">`;
        html += `<p>${lastIllnessEntry.description}</p>`;
        if (lastIllnessEntry.diagnoses && lastIllnessEntry.diagnoses.length > 0) {
            const diagList = lastIllnessEntry.diagnoses.map(d => `• ${d.cie10Code} - ${d.description}`).join('<br/>');
            html += `<div style="background: #f9f9f9; padding: 5px; border: 1px dashed #999; margin-top: 5px;"><strong>DIAGNÓSTICOS:</strong><br/>${diagList}</div>`;
        }
        html += `</div>`;
    } else if (lastEvo.evolutionText) {
         html += `<div style="${sectionTitleStyle}">2. ENFERMEDAD ACTUAL / MOTIVO (De Evolución)</div>`;
         html += `<div style="${textStyle}"><p>${lastEvo.evolutionText}</p></div>`;
    }

    // 3. EXAMEN FÍSICO
    html += `<div style="${sectionTitleStyle}">3. EXAMEN FÍSICO Y SIGNOS VITALES</div>`;
    html += `<div style="${textStyle}">`;
    if (lastEvo.id) {
        html += `
        <table style="width: 100%; margin-bottom: 10px; font-size: 10pt; background: #f4f6f8;">
            <tr>
                <td style="${vitalBoxStyle}"><strong>Tensión:</strong><br/>${lastEvo.systolic || '--'}/${lastEvo.diastolic || '--'}</td>
                <td style="${vitalBoxStyle}"><strong>F. Cardíaca:</strong><br/>${lastEvo.heartRate || '--'}</td>
                <td style="${vitalBoxStyle}"><strong>Temp:</strong><br/>${lastEvo.temperature || '--'} °C</td>
                <td style="${vitalBoxStyle}"><strong>Peso:</strong><br/>${lastEvo.weight || '--'} kg</td>
                <td style="${vitalBoxStyle}"><strong>IMC:</strong><br/>${lastEvo.bmi || '--'}</td>
            </tr>
        </table>`;
    }
    if (lastPhysicalEntry) {
        html += `<p><strong>Exploración Física (${new Date(lastPhysicalEntry.date).toLocaleDateString()}):</strong> <br/>${lastPhysicalEntry.description}</p>`;
        if (lastPhysicalEntry.labsAttached) {
             const labs = lastPhysicalEntry.labsAttached;
             html += `<div style="margin-top:5px; font-size: 10pt; background:#e8f5e9; padding:5px;"><strong>PARACLÍNICOS VINCULADOS:</strong><br/>`;
             Object.entries(labs).forEach(([key, val]) => {
                 if (key !== 'fecha' && key !== 'fecha_display' && key !== '_orden') {
                     html += `• <b>${key}:</b> ${val} &nbsp; `;
                 }
             });
             html += `</div>`;
        }
    } else {
        html += `<p><em>Sin reseña detallada reciente.</em></p>`;
    }
    html += `</div>`;

    // 4. PLAN Y TRATAMIENTO
    html += `<div style="${sectionTitleStyle}">4. PLAN, TRATAMIENTO Y PENDIENTES</div>`;
    html += `<div style="${textStyle}">`;
    if (lastRecipe && lastRecipe.medications && lastRecipe.medications.length > 0) {
        html += `<p><strong>Farmacología Indicada (${new Date(lastRecipe.createdAt).toLocaleDateString()}):</strong></p><ul style="margin-top: 0;">`;
        lastRecipe.medications.forEach(med => {
            html += `<li><strong>${med.commercialName || med.name}</strong> ${med.dosage || med.dose || ''}</li>`;
        });
        html += `</ul>`;
    }
    if (activePendings && activePendings.length > 0) {
        html += `<div style="border: 1px solid orange; background: #fff8e1; padding: 10px; margin-top: 10px;">`;
        html += `<strong style="color: #f57c00;">⚠ ESTUDIOS / PENDIENTES:</strong>`;
        html += `<ul style="margin-bottom: 0;">`;
        activePendings.forEach(p => {
            html += `<li>${p.text || p.description} <span style="font-size: 9pt; color: #666;">(${new Date(p.date || p.createdAt).toLocaleDateString()})</span></li>`;
        });
        html += `</ul></div>`;
    } else {
        html += `<p><em>No hay alertas activas.</em></p>`;
    }
    html += `</div>`;

    // 5. ANÁLISIS
    html += `<div style="${sectionTitleStyle}">5. ANÁLISIS MÉDICO INTEGRAL</div>`;
    html += `<p style="${textStyle}">Escriba aquí sus conclusiones...</p>`;

    return html;
};

// --- COMPONENTE PRINCIPAL ---
const PagedHistory = () => {
  const { user } = useAuth();
  const { patientId } = useParams();
  const navigate = useNavigate();
  
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState(''); 
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  
  const [pages, setPages] = useState([{ id: 1, content: '' }]);
  const [headerData, setHeaderData] = useState({ logoUrl: null, patient: null, footerText: '' });
  const [reportMeta, setReportMeta] = useState({ title: 'Historia Médica Integral', reason: 'Resumen Clínico' });
  
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const fileInputRef = useRef(null);
  const contentImageInputRef = useRef(null); 
  const printContainerRef = useRef(null);

  // --- CARGA INICIAL ---
  useEffect(() => {
    const initData = async () => {
      setIsInitializing(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      try {
        const [resConfig, resPatient, resEvolutions, resRecipes] = await Promise.all([
            fetch(`${API_URL}/api/config`, { headers }),
            fetch(`${API_URL}/api/patients/${patientId}`, { headers }),
            fetch(`${API_URL}/api/evolutions/${patientId}`, { headers }),
            fetch(`${API_URL}/api/recipes?patientId=${patientId}`, { headers })
        ]);

        if (resConfig.ok) {
          const confData = await resConfig.json();
          const conf = Array.isArray(confData) ? confData[0] : confData;
          const logoRaw = conf.logoCartaUrl || conf.logoUrl || conf.headerImageUrl;
          let logoB64 = null;
          if (logoRaw) logoB64 = await imageToBase64(processImageUrl(logoRaw));
          
          setHeaderData(prev => ({ ...prev, logoUrl: logoB64, footerText: conf.footerAddress }));
        }

        if (resPatient.ok) {
            const patient = await resPatient.json();
            setHeaderData(prev => ({ ...prev, patient }));

            const historyData = patient.medicalHistory || {}; 
            
            let evolutions = resEvolutions.ok ? await resEvolutions.json() : [];
            evolutions = Array.isArray(evolutions) ? evolutions.sort((a, b) => new Date(b.date) - new Date(a.date)) : [];

            let recipes = resRecipes.ok ? await resRecipes.json() : [];
            recipes = Array.isArray(recipes) ? recipes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
            
            const activePendings = (patient.pendingStudies || []).filter(p => !p.resolved);

            const initialContent = generateMedicalHistoryHTML(patient, historyData, evolutions, recipes, activePendings);
            setPages([{ id: 1, content: initialContent }]);
        }

      } catch (e) { console.error(e); setErrorMsg("Error cargando datos clínicos"); }
      finally { setIsInitializing(false); }
    };

    if (patientId) initData();
  }, [patientId]);

  // --- FUNCIONES ---
  const handleExit = () => navigate('/reports'); 

  const handleAddPage = () => setPages([...pages, { id: pages.length + 1, content: '' }]);
  const handlePageChange = (index, newContent) => {
    const updatedPages = [...pages]; updatedPages[index].content = newContent; setPages(updatedPages);
  };
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) { const r = new FileReader(); r.onload = (ev) => setHeaderData(p => ({ ...p, logoUrl: ev.target.result })); r.readAsDataURL(file); }
  };
  const handleContentImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && window.tinymce?.activeEditor) {
      const r = new FileReader();
      r.onload = (ev) => window.tinymce.activeEditor.insertContent(`<img src="${ev.target.result}" width="48%" style="margin:5px;" />`);
      r.readAsDataURL(file);
    }
    e.target.value = ''; 
  };

  const handleInsertSignature = async () => {
    setProcessStatus("Firmando..."); setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const resUser = await fetch(`${API_URL}/api/users/${user.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      let freshUser = user;
      if (resUser.ok) freshUser = await resUser.json();
      
      const rawSig = freshUser.signature_url || freshUser.signatureUrl || user?.signature_url;
      let finalSig = null;
      if (rawSig) finalSig = await imageToBase64(processImageUrl(rawSig));

      const name = `${freshUser.prefix || 'Dr.'} ${freshUser.name} ${freshUser.lastname}`;
      const imgHtml = finalSig ? `<img src="${finalSig}" width="150" style="display:block;margin:0 auto;max-width:150px;border:none;"/>` : `<div style="text-align:center;font-size:9px;color:#999;">(Sin firma)</div>`;
      
      const sigHTML = `
        <div style="text-align: center; font-family: Arial; margin-top: 15px; page-break-inside: avoid;">
          <div>${imgHtml}</div>
          <div style="border-top: 1px solid #666; width: 180px; margin: 2px auto;"></div>
          <div style="line-height: 1.1;">
            <strong style="font-size: 9pt; text-transform: uppercase;">${name}</strong><br>
            <span style="font-size: 8pt;">${freshUser.specialty || 'Médico Cirujano'}</span><br>
            <span style="font-size: 7pt;">C.I. ${freshUser.cedula || ''} | MPPS: ${freshUser.mpps_number || ''} | CMA: ${freshUser.college_number || ''}</span>
          </div>
        </div>`;
      handlePageChange(pages.length - 1, pages[pages.length - 1].content + sigHTML);
    } catch(e) { console.error(e); } finally { setIsProcessing(false); }
  };

  const handleSaveToDB = async () => {
      if (!headerData.patient) return setErrorMsg("Error: Paciente no identificado");
      setOpenSaveDialog(false); setIsProcessing(true); setProcessStatus("Guardando...");
      try {
          const fullContent = pages.map(p => p.content).join('<div style="page-break-after: always;"></div>');
          const payload = {
              title: reportMeta.title,
              reason: reportMeta.reason,
              content: fullContent,
              patientId: headerData.patient.id,
              createdBy: user.id,
              headerData: { medicalHistory: {}, evolutions: [], recipes: [] } 
          };
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/reports`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify(payload)
          });
          if(res.ok) { setSuccessMsg("✅ Historia Integral Guardada."); setTimeout(() => setIsProcessing(false), 1000); } 
          else { throw new Error("Falló guardado"); }
      } catch (e) { setErrorMsg("Error al guardar"); setIsProcessing(false); }
  };

  const handleSaveToGestion = async () => {
    if (!window.confirm("¿Generar PDF y subir a la nube?")) return;
    setIsProcessing(true); setProcessStatus("Iniciando motor PDF...");
    try {
        const doc = new jsPDF('p', 'mm', 'letter');
        const elements = document.querySelectorAll('.print-page'); 
        for (let i = 0; i < elements.length; i++) {
            setProcessStatus(`Procesando página ${i + 1}/${elements.length}...`);
            await new Promise(r => setTimeout(r, 100));
            if (i > 0) doc.addPage();
            
            const canvas = await html2canvas(elements[i], { 
                scale: 2, useCORS: true, backgroundColor: '#fff', logging: false,
                onclone: (clonedDoc) => {
                    const safetyLines = clonedDoc.querySelectorAll('.safety-guide');
                    safetyLines.forEach(el => el.style.display = 'none');
                    const editorBodies = clonedDoc.querySelectorAll('.mce-content-body');
                    editorBodies.forEach(el => {
                        el.style.border = 'none';
                        el.style.backgroundColor = 'transparent';
                        el.style.boxShadow = 'none';
                    });
                    const style = clonedDoc.createElement('style');
                    style.innerHTML = `.mce-content-body::before { display: none !important; content: "" !important; }`;
                    clonedDoc.head.appendChild(style);
                }
            });
            doc.addImage(canvas.toDataURL('image/jpeg', 0.9), 'JPEG', 0, 0, 215.9, 279.4);
        }
        setProcessStatus("Subiendo PDF...");
        const formData = new FormData();
        formData.append('file', doc.output('blob'), `Historia_${headerData.patient?.lastName}_${Date.now()}.pdf`);
        formData.append('patientId', headerData.patient?.id);
        formData.append('title', reportMeta.title);
        formData.append('category', 'HISTORIA');
        const res = await fetch(`${API_URL}/api/documents`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: formData });
        if(res.ok) { setSuccessMsg("PDF en la nube."); setTimeout(()=>setIsProcessing(false), 1500); }
        else throw new Error();
    } catch(e) { setErrorMsg("Error en PDF"); setIsProcessing(false); }
  };

  return (
    <>
      <style>{`
        @page { size: letter; margin: 0mm; } 
        @media print { body * { visibility: hidden; } .print-page-wrapper, .print-page-wrapper * { visibility: visible; } .print-page-wrapper { position: absolute; left: 0; top: 0; width: 100%; } } 
        #toolbar-container .tox-tinymce-inline { border: none !important; box-shadow: none !important; } 
        #toolbar-container .tox-editor-header { background: transparent !important; box-shadow: none !important; padding: 0 !important; } 
        .mce-content-body { border: 1px dashed #ccc; min-height: 100%; padding: 5px; background-color: #fafafa; border-radius: 4px; } 
        .mce-content-body:focus { background-color: #fff; border: 1px solid #1976d2; outline: none; }
      `}</style>

      <Backdrop sx={{ color: '#fff', zIndex: 99999, flexDirection: 'column', gap: 2, bgcolor: 'rgba(0,0,0,0.9)' }} open={isInitializing}>
          <CircularProgress color="inherit" size={80} />
          <Typography variant="h5">Generando Historia Integral...</Typography>
      </Backdrop>

      <Backdrop sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: 2 }} open={isProcessing}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h5">{processStatus}</Typography>
      </Backdrop>

      <Box className="no-print" sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'white', borderBottom: '1px solid #ccc', boxShadow: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
            <Button variant="contained" color="secondary" startIcon={<ExitIcon />} onClick={handleExit} sx={{ mr: 2, fontWeight: 'bold', bgcolor: '#d32f2f' }}>SALIR</Button>
            <div style={{ width: '1px', height: '20px', background: '#ccc', margin: '0 5px' }}></div>
            <Button variant="outlined" size="small" onClick={() => fileInputRef.current.click()}>Logo</Button>
            <DictationButton /> 
            <Button variant="outlined" size="small" startIcon={<PhotoIcon />} onClick={() => contentImageInputRef.current.click()}>Imagen</Button>
            <Button variant="outlined" size="small" startIcon={<DrawIcon />} onClick={handleInsertSignature} disabled={isProcessing}>Firma</Button>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddPage}>Pag +</Button>
            <div style={{ flex: 1 }}></div>
            <Button variant="contained" color="success" size="small" startIcon={<SaveIcon />} onClick={() => setOpenSaveDialog(true)} disabled={isProcessing}>Guardar</Button>
            <Button variant="contained" color="info" size="small" startIcon={<CloudUploadIcon />} onClick={handleSaveToGestion} disabled={isProcessing}>NUBE PDF</Button>
            <Button variant="contained" color="primary" size="small" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={isProcessing}>IMPRIMIR</Button>
         </Box>
         <Box id="toolbar-container" sx={{ minHeight: '40px', bgcolor: '#f4f6f8', display: 'flex', alignItems: 'center', pl: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic', mr: 2 }}>(Editor de Historia Integral)</Typography>
         </Box>
         <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleLogoUpload} />
         <input type="file" ref={contentImageInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleContentImageUpload} />
      </Box>

      <Box sx={{ bgcolor: '#525659', minHeight: 'calc(100vh - 110px)', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', overflowY: 'auto' }}>
        {pages.map((page, index) => (
          <div key={index} className="print-page-wrapper" ref={index === 0 ? printContainerRef : null}>
            <FixedPage pageNumber={index + 1} totalPages={pages.length} headerData={headerData}>
              <EditorComponent value={page.content} onChange={(val) => handlePageChange(index, val)} />
            </FixedPage>
          </div>
        ))}
      </Box>

      <Dialog open={openSaveDialog} onClose={() => setOpenSaveDialog(false)}>
        <DialogTitle>Guardar Historia Integral</DialogTitle>
        <DialogContent>
            <TextField autoFocus margin="dense" label="Título" fullWidth value={reportMeta.title} onChange={(e) => setReportMeta({...reportMeta, title: e.target.value})} />
            <TextField margin="dense" label="Descripción" fullWidth multiline rows={2} value={reportMeta.reason} onChange={(e) => setReportMeta({...reportMeta, reason: e.target.value})} />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenSaveDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveToDB} variant="contained" color="success">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!successMsg} autoHideDuration={4000} onClose={() => setSuccessMsg('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}><Alert severity="success">{successMsg}</Alert></Snackbar>
      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}><Alert severity="error">{errorMsg}</Alert></Snackbar>
    </>
  );
};

export default PagedHistory;